/**
 * Dark, isometric terrain mosaic used by the entire city ground plane.
 * Draw one small repeatable texture: never create a sprite for each map tile.
 * The palette follows the slate / deep teal ground in the editor reference.
 */
export const CITY_GROUND_BACKGROUND = 0x2f4147;
export const GROUND_PATTERN_SIZE = 512;

const DIAMOND_HALF_WIDTH = 128;
const DIAMOND_HALF_HEIGHT = 64;
const DIAMOND_SHADES = [
  "#2f4147", "#2d3d43", "#304147", "#2d4248",
  "#2f3f4a", "#32424b", "#2e3f46", "#304345",
  "#304349", "#2d4149", "#354248", "#314249",
  "#32424a", "#2f434b", "#35454c", "#334446",
];

function wrapped(value: number, period: number): number {
  return ((value % period) + period) % period;
}

function shadeFor(tileX: number, tileY: number): string {
  // The same diamond retains its shade in every repeated 512px tile.
  // Shifting a diamond by 512px horizontally changes (i-j) by 4;
  // shifting it vertically changes (i+j) by 8.
  const u = wrapped(tileX - tileY, 4);
  const v = wrapped(tileX + tileY, 8);
  const hash = (Math.imul(u + 13, 1103515245) ^ Math.imul(v + 29, 12345)) >>> 0;
  return DIAMOND_SHADES[hash % DIAMOND_SHADES.length];
}

export function createCityGroundPattern(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = GROUND_PATTERN_SIZE;
  canvas.height = GROUND_PATTERN_SIZE;
  const context = canvas.getContext("2d");
  if (!context) return canvas;

  context.fillStyle = "#2f4147";
  context.fillRect(0, 0, canvas.width, canvas.height);

  // A genuine 2:1 isometric diamond lattice, not square noise or a grid
  // overlay. The base layer repeats for the entire ground including the
  // enlarged outskirts, while roads, plots, shops and assets draw above it.
  for (let i = -9; i <= 9; i += 1) {
    for (let j = -9; j <= 9; j += 1) {
      const centerX = (i - j) * DIAMOND_HALF_WIDTH;
      const topY = (i + j) * DIAMOND_HALF_HEIGHT;
      if (centerX + DIAMOND_HALF_WIDTH < 0 ||
          centerX - DIAMOND_HALF_WIDTH > GROUND_PATTERN_SIZE ||
          topY + DIAMOND_HALF_HEIGHT * 2 < 0 ||
          topY > GROUND_PATTERN_SIZE) continue;

      const shade = shadeFor(i, j);
      context.beginPath();
      context.moveTo(centerX, topY);
      context.lineTo(centerX + DIAMOND_HALF_WIDTH, topY + DIAMOND_HALF_HEIGHT);
      context.lineTo(centerX, topY + DIAMOND_HALF_HEIGHT * 2);
      context.lineTo(centerX - DIAMOND_HALF_WIDTH, topY + DIAMOND_HALF_HEIGHT);
      context.closePath();
      context.fillStyle = shade;
      context.fill();
      // Cover antialiased gaps between adjacent diamonds without outlines.
      context.strokeStyle = shade;
      context.lineWidth = 1;
      context.stroke();
    }
  }

  // Sparse, small pixel-like flecks seen between the diamonds in the
  // reference. Fixed PRNG seed means no visual jump after editor saves.
  let seed = 28741;
  const random = () => {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  context.fillStyle = "rgba(24, 41, 47, 0.50)";
  for (let index = 0; index < 18; index += 1) {
    const x = 5 + Math.floor(random() * (GROUND_PATTERN_SIZE - 12));
    const y = 5 + Math.floor(random() * (GROUND_PATTERN_SIZE - 12));
    const size = index % 5 === 0 ? 4 : 2;
    context.fillRect(x, y, size, size);
  }

  return canvas;
}
